import * as React from 'react';
import { noDefault } from 'lib/eventWrappers';

import SuperCritical from 'lib/supercritical';

import { AddPortCallVesselTab as VesselTab, VesselType } from './vessel';

import { LocalVesselSearchView } from 'portcall/localvesselsearch/view';
import { NewVesselView } from 'portcall/newvessel/view';
import { ToggleButton } from 'portcall/togglebutton/view';
import { ToggleableMeasurementView } from 'portcall/toggleablemeasurement/view';
import { UserSelectorView } from 'portcall/userselector/view';

export const AddPortCallVesselTabView = SuperCritical(({ model: m } : { model : VesselTab }) =>
    <div className="addportcallvesseltab">
        <Vessel model={m} />
        <PortsOfCall model={m} />
        <Agent model={m} />
    </div>
);

const Vessel = SuperCritical(({ model: m } : { model : VesselTab }) =>
    <div className="vessel-section">
        <label className="vessel-section-label">Vessel</label>
        <div className="vessel-section-content">
            <ExistingVessel model={m} />
            <ImportVessel model={m} />
            <LengthOnlyVessel model={m} />
        </div>
    </div>
);

const ExistingVessel = SuperCritical(({ model: m } : { model : VesselTab }) =>
    m.VesselType() === VesselType.Existing ?
        <div className="existing-vessel">
            <div className="vessel-search-shell">
                <LocalVesselSearchView model={m.LocalSearch} placeholder="Required" tabIndex={1} specificInput="addPortCall" />
                {!m.PortCall.Vessel() &&
                    <div className="pc-text-smalllabel vessel-search-instructions">
                        Search a vessel by name, IMO, call sign, or MMSID
                    </div>
                }
                <ExistingVesselProperties model={m} />
            </div>
            <div className="type-selector">
                <a href="" onClick={noDefault(() => m.VesselType(VesselType.Import))}>Can't find the right vessel?</a>
                <br />
                <br />
                <span className="pc-text-smalllabel">Don't know which vessel yet?</span>
                <br />
                <a href="" onClick={noDefault(() => m.VesselType(VesselType.LengthOnly))}>Reserve by length.</a>
            </div>
        </div> :
        null
);

const ExistingVesselProperties = SuperCritical(({ model: m } : { model : VesselTab }) => {
    const v = m.PortCall.Vessel();
    return v ?
        <div className="vessel-properties">
            <div className="vessel-prop-col">
                <div className="vessel-prop">
                    <label className="vessel-prop-label">IMO</label>
                    <span>{v.IMO() || '--'}</span>
                </div>
                {(v.CallSign() || v.MMSID() || v.VIN()) &&
                    <div className="vessel-prop">
                        <label className="vessel-prop-label">
                            {
                                v.CallSign() ? 'Call Sign' :
                                v.MMSID()    ? 'MMSID'     :
                                v.VIN()      ? 'VIN'       :
                                null
                            }
                        </label>
                        <span>{v.CallSign() || v.MMSID() || v.VIN()}</span>
                    </div>
                }
            </div>
            <div className="vessel-prop-col">
                <div className="vessel-prop">
                    <label className="vessel-prop-label">LOA</label>
                    <span>{v.Length.toString({ places: 0 })}</span>
                </div>
            </div>
            <div className="vessel-prop-col">
                <div className="vessel-prop">
                    <label className="vessel-prop-label">Type</label>
                    <span>{v.VesselType() || '--'}</span>
                </div>
                <div className="vessel-prop">
                    <label className="vessel-prop-label">Flag</label>
                    <span>{v.Flag()?.Name() ?? '--'}</span>
                </div>
            </div>
        </div> :
        null;
});

const ImportVessel = SuperCritical(({ model: m } : { model : VesselTab }) =>
    m.NewVessel() &&
        <div className="import-vessel">
            <NewVesselView model={m.NewVessel()!} prevSearch={m.LocalSearch.Typeahead.data()} />
        </div>
);

const LengthOnlyVessel = SuperCritical(({ model: m } : { model : VesselTab }) =>
    m.VesselType() === VesselType.LengthOnly ?
        <div className="length-only-vessel">
            <div className="pc-text-smalllabel manual-description">
                Enter the approximate length.
                <a href="" onClick={noDefault(() => m.VesselType(VesselType.Existing))}>Back to search</a>
            </div>
            <div className="vessel-info reserve-by-length">
                <div className="length">
                    <span className="pc-text-smalllabel loa-label">Length Overall</span>
                    <ToggleableMeasurementView model={m.ToggleableLengthOnlyLength} placeholder="Required" />
                </div>
                <div className="uomselector uom">
                    <ToggleButton checked={m.LengthOnlyLengthInFeet} onLabel="Feet" offLabel="Meters" />
                </div>
            </div>
        </div> :
        null
);

const PortsOfCall = SuperCritical(({ model: m } : { model : VesselTab }) =>
    <div className="ports-of-call-section">
        <div className="port-of-call-shell">
            <label className="pc-text-smalllabel vessel-tab-aligned-label">Last</label>
            <input
                className="port-of-call"
                type="text"
                placeholder="Port of Call"
                value={m.PortCall.LastPortOfCall() ?? ''}
                onChange={e => m.PortCall.LastPortOfCall(e.currentTarget.value)}
                tabIndex={100}
            />
        </div>
        <div className="port-of-call-shell">
            <label className="pc-text-smalllabel vessel-tab-aligned-label">Next</label>
            <input
                className="port-of-call"
                type="text"
                placeholder="Port of Call"
                value={m.PortCall.NextPortOfCall() ?? ''}
                onChange={e => m.PortCall.NextPortOfCall(e.currentTarget.value)}
                tabIndex={101}
            />
        </div>
    </div>
);

const Agent = SuperCritical(({ model: m } : { model : VesselTab }) =>
    <div className="agent-section">
        <label className="pc-text-smalllabel vessel-tab-aligned-label">Agent</label>
        <div className="agent-selector-shell">
            <UserSelectorView
                model={m.AgentSelector}
                className="search-filter"
                placeholder="Required"
                tabIndex={200}
            />
            {m.PortCall.Agent() &&
                <div className="agent-details">
                    <div className="agent-company">{m.PortCall.Agent()!.Organization()?.Name()}</div>
                    <div className="agent-phone">
                        {m.PortCall.Agent()!.PhoneNumbers().length > 0 ?
                            m.PortCall.Agent()!.PhoneNumbers()[0].Number() :
                            ''
                        }
                    </div>
                    {m.PortCall.Agent()?.User() &&
                        <div className="agent-email-shell">
                            <a
                                className="agent-email"
                                href={`mailto:${m.PortCall.Agent()!.User()!.Email()}`}
                            >
                                {m.PortCall.Agent()!.User()!.Email()}
                            </a>
                        </div>
                    }
                </div>
            }
        </div>
        {m.User() && !m.PortCall.Agent() &&
            <div className="use-my-profile-shell">
                <a
                    className="use-my-profile"
                    href=""
                    onClick={noDefault(() => m.PortCall.AgentId(m.User()!.Id()))}
                    tabIndex={300}
                >
                    Use my profile as agent
                </a>
            </div>
        }
    </div>
);



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/vesselview.tsx